import { useState } from 'react';
import type { PayeeBreakdownItem } from '../../../hooks/useReports';

interface Props {
  payeeBreakdown: PayeeBreakdownItem[];
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const PayeeBreakdown = ({ payeeBreakdown, loading }: Props) => {
  const [activePayee, setActivePayee] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-gray-50 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (payeeBreakdown.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-user-3-line text-2xl text-emerald-400" />
        </div>
        <p className="text-[15px] font-600 text-gray-700 mb-1">Sem dados de favorecidos</p>
        <p className="text-[13px] text-gray-400 max-w-xs">
          Vincule favorecidos aos seus lançamentos para ver relatórios detalhados aqui.
        </p>
      </div>
    );
  }

  const maxTotal = payeeBreakdown[0]?.total ?? 1;
  const visible = showAll ? payeeBreakdown : payeeBreakdown.slice(0, 8);
  const selected = activePayee ? payeeBreakdown.find(p => p.payee_name === activePayee) : null;
  const totalSpent = payeeBreakdown.reduce((s, p) => s + p.total, 0);
  const withTransactions = payeeBreakdown.length;

  return (
    <div className="space-y-5">
      {/* — Summary Cards — */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
              <i className="ri-user-3-line text-emerald-500 text-sm" />
            </div>
            <span className="text-xs text-gray-400 font-500">Favorecidos com despesas</span>
          </div>
          <p className="text-xl font-700 text-gray-900">{withTransactions}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">no período selecionado</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-red-400 text-sm" />
            </div>
            <span className="text-xs text-gray-400 font-500">Total gasto</span>
          </div>
          <p className="text-xl font-700 text-red-500">{fmt(totalSpent)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">soma de todos os favorecidos</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-line text-amber-500 text-sm" />
            </div>
            <span className="text-xs text-gray-400 font-500">Maior favorecido</span>
          </div>
          <p className="text-base font-700 text-gray-900 truncate">{payeeBreakdown[0]?.payee_name ?? '—'}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">{fmt(payeeBreakdown[0]?.total ?? 0)} no período</p>
        </div>
      </div>

      {/* — Ranking — */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <h3 className="text-[15px] font-700 text-gray-900">Ranking de Gastos por Favorecido</h3>
          <p className="text-[12px] text-gray-400 mt-0.5">Clique em um favorecido para ver a evolução mensal</p>
        </div>

        <div className="divide-y divide-gray-50">
          {visible.map((item, idx) => {
            const isActive = activePayee === item.payee_name;
            const barWidth = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;

            return (
              <div key={item.payee_name}>
                <button
                  onClick={() => setActivePayee(isActive ? null : item.payee_name)}
                  className={`w-full text-left px-5 py-3.5 hover:bg-gray-50/80 transition-colors cursor-pointer ${isActive ? 'bg-emerald-50/40' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Rank */}
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-[11px] font-700 ${
                      idx === 0 ? 'bg-amber-100 text-amber-600' :
                      idx === 1 ? 'bg-gray-100 text-gray-500' :
                      idx === 2 ? 'bg-orange-100 text-orange-500' :
                      'bg-gray-50 text-gray-400'
                    }`}>
                      {idx + 1}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] font-600 text-gray-800 truncate">{item.payee_name}</span>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          <span className="text-[11px] text-gray-400">{item.count} lançamento{item.count !== 1 ? 's' : ''}</span>
                          <span className="text-[11px] text-gray-400">{item.percent}%</span>
                          <span className="text-[13px] font-700 text-red-500">{fmt(item.total)}</span>
                          <i className={`text-[11px] text-gray-300 ${isActive ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: idx === 0 ? '#F59E0B' : idx === 1 ? '#6B7280' : idx === 2 ? '#F97316' : '#10B981',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </button>

                {/* Monthly evolution (expanded) */}
                {isActive && item.monthlyData.length > 0 && (
                  <div className="px-5 pb-4 bg-emerald-50/30 border-t border-emerald-100/60">
                    <p className="text-[11px] text-gray-400 font-600 uppercase tracking-wider mt-3 mb-3">
                      Evolução mensal — {item.payee_name}
                    </p>
                    <div className="grid gap-2">
                      {item.monthlyData.map(m => {
                        const mMax = Math.max(...item.monthlyData.map(x => x.total), 1);
                        const mPct = mMax > 0 ? (m.total / mMax) * 100 : 0;
                        return (
                          <div key={m.month} className="flex items-center gap-3">
                            <span className="text-[11px] text-gray-400 w-14 shrink-0">{m.monthLabel}</span>
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-400 rounded-full transition-all duration-300"
                                style={{ width: `${mPct}%` }}
                              />
                            </div>
                            <span className={`text-[11px] font-600 w-24 text-right shrink-0 ${m.total > 0 ? 'text-gray-700' : 'text-gray-300'}`}>
                              {m.total > 0 ? fmt(m.total) : '—'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center justify-between">
                      <span className="text-[11px] text-gray-400">Média mensal</span>
                      <span className="text-[12px] font-700 text-emerald-600">{fmt(item.avgPerMonth)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {payeeBreakdown.length > 8 && (
          <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/30">
            <button
              onClick={() => setShowAll(v => !v)}
              className="text-[12px] text-emerald-600 font-600 hover:underline cursor-pointer flex items-center gap-1.5"
            >
              <i className={`text-[13px] ${showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              {showAll ? 'Mostrar menos' : `Ver todos os ${payeeBreakdown.length} favorecidos`}
            </button>
          </div>
        )}
      </div>

      {/* — Monthly Evolution Table (all payees overview) — */}
      {payeeBreakdown.length > 0 && payeeBreakdown[0].monthlyData.length > 1 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h3 className="text-[15px] font-700 text-gray-900">Evolução Mensal — Top Favorecidos</h3>
            <p className="text-[12px] text-gray-400 mt-0.5">Comparativo dos principais favorecidos mês a mês</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-2.5 text-left text-[11px] font-600 text-gray-500 uppercase tracking-wider w-40">Favorecido</th>
                  {payeeBreakdown[0].monthlyData.map(m => (
                    <th key={m.month} className="px-3 py-2.5 text-right text-[11px] font-600 text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {m.monthLabel}
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-right text-[11px] font-600 text-gray-500 uppercase tracking-wider whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payeeBreakdown.slice(0, 8).map(item => (
                  <tr key={item.payee_name} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5">
                      <span className="text-[12px] font-600 text-gray-800 truncate block max-w-[140px]">{item.payee_name}</span>
                    </td>
                    {item.monthlyData.map(m => (
                      <td key={m.month} className={`px-3 py-2.5 text-right text-[11px] whitespace-nowrap ${m.total > 0 ? 'text-gray-700 font-500' : 'text-gray-300'}`}>
                        {m.total > 0 ? fmt(m.total) : '—'}
                      </td>
                    ))}
                    <td className="px-4 py-2.5 text-right text-[12px] font-700 text-red-500 whitespace-nowrap">
                      {fmt(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
